/**
 * Logs messages for debugging, info, warning, and error notifications.
 * Silent by default until Logger.level is set to higher or equal to DebugLevel.DEBUG
 *
 * @namespace Logger
 * @class logger
 * @static
 */
(function (root, factory) {
  // Support both AMD and browser globals.
  if (typeof define === 'function' && define.amd) {
    // AMD is present. Register as an anonymous module.
    define(['./debugLevel'], factory);
  } else if (typeof exports === 'object') {
    // CommonJS-style exports
    var debugLevel = require('./debugLevel');
    module.exports = factory(debugLevel);
  } else {
    throw 'Only AMD and CommonJS imports are supported.';
  }
}(this, function(DebugLevel) {
  'use strict';
  //Tell IE9 to use its built-in console
  if (Function.prototype.bind && /^object$|^function$/.test(typeof console) && typeof console.log === 'object'
      && typeof window.addEventListener === 'function') {
    ['log', 'info', 'warn', 'error', 'assert', 'dir', 'clear', 'profile', 'profileEnd'].forEach(function(method) {
      console[method] = this.call(console[method], console);
    }, Function.prototype.bind);
  }

  return {
    level: -1,
    addLineNumber: false,
    fireFunctions: false,

    /**
     * Initializes logger to the level of logging
     * @method init
     * @param  {DebugLevel} debugLevel to set verbosity of logger, See {@link DebugLevel}.
     * @param  {Boolean} addLineNumber whether or not to include line number at the end of each log line. Only available in Chrome
     * @param  {Boolean} useFirebug whether or not to inject firebug-lite into the DOM for debugging purposes
     */
    init: function(debugLevel, addLineNumber, useFirebug) {
      this.level = debugLevel;
      this.addLineNumber = addLineNumber;

      if (useFirebug) {
        this.injectFirebug();
      }
    },

    /**
     * Injects Firebug Lite into the DOM
     * @method injectFirebug
     * @private
     */
    injectFirebug: function() {
      if (!document.getElementById('firebug-lite')) {
        // Include the script
        (function () {
          var script = document.createElement('script');
          script.type = 'text/javascript';
          script.id = 'firebug-lite';
          // If you run the script locally, change this to /path/to/firebug-lite/build/firebug-lite.js
          script.src = 'https://getfirebug.com/firebug-lite.js';
          // If you want to expand the console window by default, uncomment this line
          //document.getElementsByTagName('HTML')[0].setAttribute('debug','true');
          document.getElementsByTagName('HEAD')[0].appendChild(script);
        }());
      }
    },

    /**
     * Logs out logged message as well as the line number/file name/function where logger was called
     * @method log
     */
    log: function() {
      var args, processedArgs, fileLineNumber, sliced;
      args = Array.prototype.slice.call(arguments);
      processedArgs = this.processArgs(args);
      if (window.chrome && this.addLineNumber) {
        // Fake a thrown exception to get the line number from the stack.
        try {
          throw new Error();
        } catch (err) {
          // Get the filename and line number and formats it.
          fileLineNumber = (err.stack.split('\n')[3]).replace(/\(.*\//, '(');
          processedArgs.push(fileLineNumber);
        }
      }

      if (window.console && typeof console.log === 'function') {
        sliced = Array.prototype.slice.call(processedArgs);
        // Single argument, which is a string
        if (sliced.length === 1 && typeof sliced[0] === 'string') {
          console.log(sliced.toString());
        } else {
          console.log.apply(console, sliced);
        }
      }
    },

    /**
     * Replaces keys like {0}, {1}, {2}, etc. with corresponding (by index) values provided by arguments.
     * Example: Logger.debug("Hello {0}! This is an object:", "World", myObj);
     *
     * @method processArgs
     * @private
     * @param {Object[]} args the arguments. First argument is the template, the remaining arguments (up to the number used by the
     *         template) are values to replace the keys.
     * @return {Object[]} the complete message and any remaining objects to be logged.
     */
    processArgs: function(args) {
      var message, i, key, val, arg;
      if (args && args.length > 1) {
        message = args[0];
        for (i = 1; i < args.length; i++) {
          key = '{' + (i - 1) + '}';
          arg = args[i];
          if (message.indexOf(key) !== -1) {
            if (this.fireFunctions && typeof arg === 'function') {
              val = arg.apply(arg);
            } else {
              val = arg;
            }
            message = message.replace(key, val);
          } else {
            break;
          }
        }
        return [message].concat(args.splice(i, args.length));
      } else {
        return args;
      }
    },

    /**
     * Handles logging for debug level messages
     * @method debug
     */
    debug: function() {
      if (this.level <= DebugLevel.DEBUG) {
        this.log.apply(this, arguments);
      }
    },

    /**
     * Handles logging for info level messages
     * @method info
     */
    info: function() {
      if (this.level <= DebugLevel.INFO) {
        this.log.apply(this, arguments);
      }
    },

    /**
     * Handles logging for warning level messages
     * @method warn
     */
    warn: function() {
      if (this.level <= DebugLevel.WARNING) {
        this.log.apply(this, arguments);
      }
    },

    /**
     * Handles logging for error level messages
     * @method error
     */
    error: function() {
      if (this.level <= DebugLevel.ERROR) {
        this.log.apply(this, arguments);
      }
    }
  };
}));
